import React from 'react';
import { Navigate } from 'react-router-dom';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * PROTECTED ROUTE - BASADO EN PERMISOS
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Este componente verifica:
 * 1. Si el usuario está autenticado
 * 2. Si tiene los permisos necesarios para acceder a la ruta
 * 
 * Props:
 * - children: El componente a renderizar si tiene acceso
 * - requiredPermissions: Array de permisos requeridos (al menos uno)
 * - requireAll: Si es true, requiere TODOS los permisos; si es false, al menos uno
 * - allowedRoles: (Opcional) Lista de roles permitidos (para compatibilidad)
 * 
 * Ejemplos de uso:
 * 
 * // Cualquier usuario autenticado
 * <ProtectedRoute><Dashboard /></ProtectedRoute>
 * 
 * // Requiere permiso específico
 * <ProtectedRoute requiredPermissions={['verDocumentosMedicos']}>
 *   <MedicalDocuments />
 * </ProtectedRoute>
 * 
 * // Requiere al menos uno de varios permisos
 * <ProtectedRoute requiredPermissions={['verDocumentosMedicos', 'verDocumentosAdministrativos']}>
 *   <Documents />
 * </ProtectedRoute>
 * 
 * // Requiere TODOS los permisos
 * <ProtectedRoute requiredPermissions={['gestionarUsuarios', 'configurarSistema']} requireAll={true}>
 *   <AdminPanel />
 * </ProtectedRoute>
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

function ProtectedRoute({ 
  children, 
  requiredPermissions = [], 
  requireAll = false,
  allowedRoles = null  // Para compatibilidad con código existente
}) {
  // ═══════════════════════════════════════════════════════════════════════════
  // OBTENER DATOS DEL USUARIO
  // ═══════════════════════════════════════════════════════════════════════════
  const rol = localStorage.getItem('rol');
  const permisosRaw = localStorage.getItem('permisos');
  
  let permisos = {};
  try {
    permisos = permisosRaw ? JSON.parse(permisosRaw) : {};
  } catch (e) {
    console.error('❌ [ProtectedRoute] Error parseando permisos:', e);
    permisos = {};
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VERIFICAR AUTENTICACIÓN
  // ═══════════════════════════════════════════════════════════════════════════
  const isAuthenticated = localStorage.getItem('correo') || 
                          localStorage.getItem('token') || 
                          localStorage.getItem('nombres');

  if (!isAuthenticated) {
    console.log('🚫 [ProtectedRoute] No autenticado, redirigiendo a login');
    return <Navigate to="/login" replace />;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SUPERADMIN SIEMPRE TIENE ACCESO
  // ═══════════════════════════════════════════════════════════════════════════
  if (rol === 'superadmin') {
    console.log('✅ [ProtectedRoute] Acceso permitido (superadmin)');
    return children;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VERIFICAR POR ROLES (Compatibilidad con código existente)
  // ═══════════════════════════════════════════════════════════════════════════
  if (allowedRoles && allowedRoles.length > 0) {
    if (allowedRoles.includes(rol)) {
      console.log(`✅ [ProtectedRoute] Acceso permitido por rol: ${rol}`);
      return children;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VERIFICAR POR PERMISOS
  // ═══════════════════════════════════════════════════════════════════════════
  if (requiredPermissions && requiredPermissions.length > 0) {
    let hasAccess = false;

    if (requireAll) {
      // Requiere TODOS los permisos
      hasAccess = requiredPermissions.every(permiso => permisos[permiso] === true);
    } else {
      // Requiere al menos UN permiso
      hasAccess = requiredPermissions.some(permiso => permisos[permiso] === true);
    }

    if (hasAccess) {
      console.log(`✅ [ProtectedRoute] Acceso permitido por permisos`);
      return children;
    } else {
      console.log(`🚫 [ProtectedRoute] Acceso denegado. Permisos requeridos: ${requiredPermissions.join(', ')}`);
      console.log(`🚫 [ProtectedRoute] Permisos del usuario:`, permisos);
      
      // Redirigir al dashboard con mensaje
      return <Navigate to="/dashboard" state={{ 
        error: 'No tienes permisos para acceder a esta página' 
      }} replace />;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SI NO HAY RESTRICCIONES, PERMITIR ACCESO
  // ═══════════════════════════════════════════════════════════════════════════
  if (!allowedRoles && (!requiredPermissions || requiredPermissions.length === 0)) {
    console.log('✅ [ProtectedRoute] Acceso permitido (sin restricciones)');
    return children;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // POR DEFECTO, DENEGAR ACCESO
  // ═══════════════════════════════════════════════════════════════════════════
  console.log('🚫 [ProtectedRoute] Acceso denegado por defecto');
  return <Navigate to="/dashboard" state={{ 
    error: 'No tienes permisos para acceder a esta página' 
  }} replace />;
}

export default ProtectedRoute;